<i18n src="./resources/locales.json"></i18n>

<template>
  <div>
    <v-subheader class="remarks-header surface lighten-1">
      <span v-once>{{ $t('title') }}</span>
      <v-btn
        v-if="allowNewRemarks"
        absolute
        right
        small
        text
        class="mr-n4 new-remark-button"
        @click="showNewRemarkInput"
      >
        <v-icon left small>mdi-pencil</v-icon> {{ $t('newRemarkActionLabel') }}
      </v-btn>
    </v-subheader>
    <v-expand-transition>
      <v-row v-show="expandRemarkInput" class="remarks-container">
        <v-list-item class="remark-item">
          <v-list-item-avatar class="remark-avatar">
            <EcsAvatar :contact-id="loggedInAgent.id" :size="40" no-animation />
          </v-list-item-avatar>
          <v-list-item-content class="remark-content">
            <EcsSubmitInput
              ref="remarkInput"
              class="remark-input"
              :placeholder="$t('newRemarkPlaceholder')"
              :disable-multiline-shadow="true"
              @onSubmit="addRemark"
            />
          </v-list-item-content>
        </v-list-item>
      </v-row>
    </v-expand-transition>
    <v-row class="remarks-container">
      <v-list color="transparent" flat dense>
        <template v-if="remarks && remarks.length">
          <transition-group name="remarks-list">
            <v-list-item v-for="(remark, index) in remarks" :key="index + remark.createTime" class="remark-item">
              <v-list-item-avatar class="remark-avatar">
                <EcsAvatar :contact-id="remark.creatorId" :alt="remark.creator" :size="40" no-animation />
              </v-list-item-avatar>
              <v-list-item-content class="remark-content">
                <v-list-item-title>
                  <strong>{{ remark.creator }}</strong>
                  <span class="grey--text text--lighten-1 remarks-time">
                    {{ getRemarkCreationTimeAgo(remark.createTime) }}
                  </span>
                </v-list-item-title>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <v-list-item-subtitle class="remark-text" v-html="remark.text"> </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </transition-group>
        </template>
        <v-list-item v-else>
          <v-list-item-content class="remark-content">
            <v-list-item-subtitle v-once class="remark-text">
              {{ $t('emptyRemarks') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-row>

    <!-- Past Remarks -->
    <template v-if="oldRemarks && oldRemarks.length">
      <v-subheader v-once class="remarks-header surface lighten-1">
        {{ $t('prevTitle') }}
      </v-subheader>
      <v-row>
        <v-list color="transparent" flat dense>
          <v-list-item v-for="oldie in oldRemarks" :key="oldie.id" class="remark-item">
            <v-list-item-avatar class="remark-avatar">
              <EcsAvatar :contact-id="oldie.creatorId" :alt="oldie.creatorName" :size="40" no-animation />
            </v-list-item-avatar>
            <v-list-item-content class="remark-content">
              <v-list-item-title>
                <strong>{{ oldie.creatorName }}</strong>
                <span class="grey--text text--lighten-1 remarks-time">
                  {{ getRemarkCreationTimeAgo(oldie.createTime) }}
                </span>
              </v-list-item-title>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <v-list-item-subtitle class="remark-text" v-html="oldie.note"> </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-row>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import EcsSubmitInput from '@/components/ecs-submit-input/EcsSubmitInput';
import { parseMessage } from '@/utils';
import { NOTE_TYPE } from '@/constants/contactConstants';
import InteractionService from '@/services/interactionService';
import ContactService from '@/services/contactService';

export default {
  name: 'EcsInteractionRemarks',

  components: { EcsAvatar, EcsSubmitInput },

  props: {
    interactionId: {
      type: String,
      required: true
    },
    contactId: {
      type: String,
      default: null
    },
    allowNewRemarks: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      expandRemarkInput: false,
      remarks: [],
      oldRemarks: []
    };
  },

  computed: mapGetters('AGENT', ['loggedInAgent']),

  created() {
    this.getRemarks();
    this.getOldRemarks();
  },

  methods: {
    async getRemarks() {
      if (!this.allowNewRemarks || !this.interactionId || !this.loggedInAgent.id) {
        this.remarks = [];
        return;
      }

      try {
        const response = await InteractionService.getRemarksByAgent(this.interactionId, this.loggedInAgent.id);
        this.remarks = response.data ? response.data.reverse() : [];
      } catch {
        this.remarks = [];
      }
    },

    async getOldRemarks() {
      let result = [];

      if (this.contactId) {
        const response = await ContactService.getContactNotes(this.contactId);
        result = response && response.data ? response.data.filter(note => note.noteType === NOTE_TYPE.remark) : [];
        this.oldRemarks = result;
      }

      return result;
    },

    showNewRemarkInput() {
      this.expandRemarkInput = !this.expandRemarkInput;

      if (!this.expandRemarkInput) return;

      setTimeout(() => {
        this.$refs.remarkInput.$el.querySelector('textarea').focus();
      }, 250);
    },

    hideRemarkInput() {
      this.expandRemarkInput = false;
    },

    addRemark(remark) {
      if (!remark) return;

      this.hideRemarkInput();

      InteractionService.addRemark(this.interactionId, parseMessage(remark))
        .then(this.getRemarks)
        .catch(() => {
          this.$root.$emit('snackbar-message', {
            message: this.$t('addNewRemarkError'),
            type: 'error'
          });
        });
    },

    getRemarkCreationTimeAgo(creationTime) {
      return moment(creationTime || Date.now()).fromNow();
    }
  }
};
</script>

<style scoped>
.remarks-header {
  height: 26px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  padding: 0;
  position: relative;
}

.remarks-container .v-list {
  max-width: 100%;
}

.new-remark-button {
  font-weight: bolder;
}

.remark-item {
  margin: 2px 0;
}

.remark-time {
  opacity: 0.8;
  font-size: 10px;
}

.remark-avatar {
  align-self: start;
  padding: 0;
}

.remark-content {
  align-self: center;
  margin: 0;
  padding: 0 !important;
}

.remark-text {
  text-overflow: initial;
  overflow: hidden;
  white-space: initial;
  word-break: break-word;
}

.remarks-list-enter-active,
.remarks-list-leave-active {
  transition: all 250ms;
}

.remarks-list-enter,
.remarks-list-leave-to {
  opacity: 0;
  transform: translateY(-36px);
}
</style>
